import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Bell, BellRing, CheckCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { calendarApi } from "#/features/calendar/api";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationsCount,
} from "../hooks";
import { NotificationCard } from "../components/NotificationCard";

type FilterValue = "ALL" | "UNREAD" | "READ";
const PAGE_SIZE = 10;

/** Lead-time options for the personal "remind me before an event" setting,
 *  independent of the event itself or of the broadcast notification. */
const REMINDER_OPTIONS = [
  { value: 15, label: "15 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 180, label: "3 hours before" },
  { value: 1440, label: "1 day before" },
  { value: 2880, label: "2 days before" },
];

function ReminderSettingsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["calendar-reminder-preference"],
    queryFn: calendarApi.getReminderPreference,
  });
  const [minutes, setMinutes] = useState<number>(60);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setMinutes(data.reminderMinutesBefore);
  }, [data]);

  const save = useMutation({
    mutationFn: (v: number) => calendarApi.setReminderPreference(v),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <BellRing className="h-4 w-4 text-emerald-600" />
        Event reminders
      </div>
      <p className="text-xs text-slate-400">Remind me, independently of event notifications:</p>
      <select
        value={minutes}
        disabled={isLoading || save.isPending}
        onChange={(e) => {
          const v = Number(e.target.value);
          setMinutes(v);
          save.mutate(v);
        }}
        className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700"
      >
        {REMINDER_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {saved && <span className="text-xs font-semibold text-emerald-600">Saved</span>}
    </div>
  );
}

export default function NotificationsPage() {
  const [page, setPage]     = useState(0);
  const [filter, setFilter] = useState<FilterValue>("ALL");

  const query    = useNotifications(page, PAGE_SIZE);
  const unreadQ  = useUnreadNotificationsCount();
  const markRead = useMarkNotificationRead();
  const markAll  = useMarkAllNotificationsRead();

  const unreadCount = unreadQ.data?.unreadCount ?? 0;

  const allItems = query.data?.content ?? [];
  const items = filter === "ALL"
    ? allItems
    : allItems.filter((n) => {
        const isUnread = n.status === "UNREAD" || (!n.readAt && n.status !== "READ");
        return filter === "UNREAD" ? isUnread : !isUnread;
      });

  const totalPages  = query.data?.totalPages    ?? 0;
  const totalElems  = query.data?.totalElements ?? 0;
  const currentPage = query.data?.number        ?? 0;
  const offset      = currentPage * PAGE_SIZE;

  function changeFilter(v: FilterValue) { setFilter(v); setPage(0); }

  if (query.isLoading) return <PageLoader label="Loading notifications…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load notifications"
        message={query.error instanceof Error ? query.error.message : "Could not load notifications."}
      />
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Reminder settings (personal, independent of event notifications) ── */}
      <ReminderSettingsPanel />

      {/* ── Header ── */}
      <div className="space-y-2">
        <h1 className="text-xl font-bold text-slate-900">Notifications</h1>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filter pills */}
          <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
            {(["ALL", "UNREAD", "READ"] as FilterValue[]).map((v) => {
              const label =
                v === "ALL"    ? `All${totalElems > 0 ? ` ${totalElems}` : ""}` :
                v === "UNREAD" ? `Unread${unreadCount > 0 ? ` ${unreadCount}` : ""}` :
                "Read";
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => changeFilter(v)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                    filter === v
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50 transition"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* Table head */}
        <div className="grid grid-cols-[2.5rem_1fr_7rem_6rem_3rem] border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>#</span>
          <span>Notification</span>
          <span>Module</span>
          <span>Time</span>
          <span />
        </div>

        {/* Rows */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
              <Bell className="h-5 w-5 text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-600">
              {filter === "UNREAD" ? "You're all caught up" : "No notifications"}
            </p>
            <p className="text-xs text-slate-400">
              {filter === "UNREAD"
                ? "No unread notifications right now."
                : "Notifications will appear here once there's activity on your account."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item, idx) => (
              <NotificationCard
                key={item.id}
                item={item}
                rowNumber={offset + idx + 1}
                onMarkRead={(id) => markRead.mutate(id)}
                markingRead={markRead.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {filter === "ALL" && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Page {currentPage + 1} of {totalPages} · {totalElems} notifications
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>

            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const start = Math.max(0, Math.min(currentPage - 3, totalPages - 7));
              const p = totalPages > 7 ? start + i : i;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`h-7 w-7 rounded-lg text-xs font-medium transition ${
                    p === currentPage
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {p + 1}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
